import { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { AlertTriangle, CheckCircle2, MailCheck, Lock } from "lucide-react";
import { api } from "../lib/api";
import { PoweredBy } from "../components/PoweredBy";
import { BETA_INVITE } from "../constants/testIds";

// Read the raw token from the URL fragment ONLY. Captured ONCE at module load
// (before React's StrictMode double-invoke) so it is never lost. The token is
// never written to localStorage, query params, analytics or logs — it lives in
// module/component state and travels to the backend only in POST bodies. The
// visible fragment is scrubbed from the address bar / history by an effect that
// runs (and retries) after load, since a scrub during initial parse does not
// always stick.
function readToken() {
  if (typeof window === "undefined") return null;
  if (!window.location.pathname.startsWith("/beta/invite")) return null;
  const match = (window.location.hash || "").match(/token=([^&]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

function scrubFragment() {
  if (typeof window === "undefined" || !window.location.hash) return;
  try {
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
  } catch (_) { /* no-op */ }
}

const INITIAL_TOKEN = readToken();

const REASON_COPY = {
  invalid: "This invitation link is not valid. Please check the link in your email, or ask for a fresh invitation.",
  expired: "This invitation has expired. Please ask your host for a fresh invitation.",
  revoked: "This invitation has been withdrawn. Please contact your host if you believe this is a mistake.",
  accepted: "This invitation has already been used. If you have an account, please sign in.",
};

export default function BetaInvite() {
  const [phase, setPhase] = useState("loading"); // loading | invalid | form | success
  const [reason, setReason] = useState(null);
  const [token, setToken] = useState(INITIAL_TOKEN);
  const [invite, setInvite] = useState(null); // { email, capability }
  const [form, setForm] = useState({ password: "", display_name: "", age_band: "" });
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(null);   // current beta_terms document
  const [privacy, setPrivacy] = useState(null); // current privacy_notice document
  const [agreed, setAgreed] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Remove the token fragment from the address bar / history. Retry a few times
  // because an early scrub during initial navigation does not always persist.
  useEffect(() => {
    scrubFragment();
    const timers = [0, 100, 400, 1200].map((ms) => setTimeout(scrubFragment, ms));
    return () => timers.forEach(clearTimeout);
  }, []);

  const validate = useCallback(async () => {
    const t = INITIAL_TOKEN;
    if (!t) { setReason("invalid"); setPhase("invalid"); return; }
    const r = await api("/invitations/validate", { method: "POST", auth: false, body: { token: t } });
    if (r.ok && r.data?.valid) {
      setInvite({ email: r.data.email, capability: r.data.capability });
      const [td, pd] = await Promise.all([
        api("/legal/documents/beta_terms", { method: "GET", auth: false }),
        api("/legal/documents/privacy_notice", { method: "GET", auth: false }),
      ]);
      if (td.ok) setTerms(td.data);
      if (pd.ok) setPrivacy(pd.data);
      setPhase("form");
    } else {
      setReason((r.data && r.data.reason) || "invalid");
      setPhase("invalid");
    }
  }, []);

  useEffect(() => { validate(); }, [validate]);

  const submit = async () => {
    if (!form.password || form.password.length < 8) return toast.error("Password must be at least 8 characters.");
    if (!form.age_band) return toast.error("Please select your age range.");
    if (!terms || !privacy) return toast.error("Legal documents could not be loaded. Please refresh and try again.");
    if (!agreed) return toast.error("Please accept the beta terms and acknowledge the privacy notice to continue.");
    setBusy(true);
    try {
      const r = await api("/auth/register", {
        method: "POST",
        auth: false,
        body: {
          token,                       // POST body only — never a URL
          email: invite.email,         // bound, fixed email
          password: form.password,
          display_name: form.display_name || null,
          age_band: form.age_band,
          accepted_terms_version: terms ? terms.version : null,
          acknowledged_privacy_version: privacy ? privacy.version : null,
        },
      });
      if (r.ok) {
        setToken(null);                // drop the token from memory once consumed
        setPhase("success");
      } else {
        toast.error(r.data?.detail || "Registration failed.");
      }
    } finally { setBusy(false); }
  };

  return (
    <>
      <Toaster position="top-right" richColors />
      <style>{`
        .beta-main{min-height:calc(100vh - 72px);display:flex;align-items:center;justify-content:center;padding:32px 20px;}
        .beta-main .card{width:100%;max-width:440px;}
        .beta-main .muted{color:var(--ww-charcoal-soft);line-height:1.6;}
        .beta-email-wrap{position:relative;}
        .beta-email-wrap .beta-lock{position:absolute;right:14px;top:50%;transform:translateY(-50%);opacity:.55;pointer-events:none;}
        .beta-email-hint{font-size:12px;color:var(--ww-charcoal-soft);margin:-4px 0 6px;}
        .beta-terms-field{display:flex;align-items:flex-start;gap:10px;text-align:left;margin:10px 0 4px;}
        .beta-terms-field > input[type=checkbox]{flex:0 0 auto;width:16px;height:16px;margin:3px 0 0;cursor:pointer;}
        .beta-terms-copy{margin:0;font-size:13px;line-height:1.55;color:var(--ww-charcoal-soft);cursor:pointer;}
        .beta-terms-copy .linklike{display:inline;text-align:left;background:none;border:0;color:inherit;font:inherit;text-decoration:underline;cursor:pointer;padding:0;white-space:normal;}
        .beta-terms-body{white-space:pre-wrap;text-align:left;font-size:12px;line-height:1.5;margin-top:8px;max-height:160px;overflow:auto;}
      `}</style>
      <div className="ww-auth">
      <div className="app" data-testid={BETA_INVITE.page}>
        <header className="hdr">
          <div className="brand auth-brand" data-testid="beta-brand">
            <img src="/brand/www-logo-primary.png" className="auth-logo"
                 alt="World Wide Wallpaper — A world of joy, freely shared." />
            <span className="tag">Closed beta</span>
          </div>
        </header>
        <main className="main beta-main">
          {phase === "loading" && (
            <div className="card" data-testid={BETA_INVITE.loading}>
              <p className="muted">Checking your invitation…</p>
            </div>
          )}

          {phase === "invalid" && (
            <div className="card" data-testid={BETA_INVITE.invalid}>
              <h3><AlertTriangle size={16} className="warn" /> Invitation unavailable</h3>
              <p className="muted">{REASON_COPY[reason] || REASON_COPY.invalid}</p>
              <a className="btn" href="/" data-testid={BETA_INVITE.loginLink}>Go to sign in</a>
            </div>
          )}

          {phase === "form" && invite && (
            <div className="card auth" data-testid="beta-invite-form">
              <h3><MailCheck size={16} /> Accept your invitation</h3>
              <p className="muted">
                You've been invited with <b data-testid={BETA_INVITE.capability}>{invite.capability}</b> access.
                Create your account to continue.
              </p>
              <label className="field-label">Invited email</label>
              <div className="beta-email-wrap">
                <input data-testid={BETA_INVITE.emailFixed} value={invite.email} readOnly disabled />
                <Lock size={14} className="beta-lock" />
              </div>
              <p className="beta-email-hint">This invitation is bound to this email and cannot be changed.</p>
              <input data-testid={BETA_INVITE.passwordInput} type="password" placeholder="Create a password (min 8)"
                     value={form.password} onChange={set("password")} />
              <input data-testid={BETA_INVITE.displayNameInput} placeholder="Display name (optional)"
                     value={form.display_name} onChange={set("display_name")} />
              <p className="beta-email-hint" data-testid="display-name-help">
                This will be proudly displayed with your submitted pictures.
              </p>
              <label className="field-label">Age range</label>
              <select data-testid={BETA_INVITE.ageBandSelect}
                      value={form.age_band} onChange={set("age_band")}>
                <option value="">Select your age range</option>
                <option value="under_18">Under 18</option>
                <option value="18_or_over">18 or over</option>
              </select>
              {terms && privacy && (
                <div className="beta-terms" data-testid="beta-terms">
                  <div className="beta-terms-field">
                    <input id="beta-terms-checkbox" type="checkbox" data-testid="beta-terms-checkbox"
                           checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
                    <label htmlFor="beta-terms-checkbox" className="beta-terms-copy muted">
                      I accept the{" "}
                      <span role="button" tabIndex={0} className="linklike" data-testid="beta-terms-toggle"
                            onClick={(e) => { e.preventDefault(); setShowTerms((s) => !s); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowTerms((s) => !s); } }}>
                        {terms.title.replace(/\s*\(DRAFT\)/, "")} ({terms.version})
                      </span>{" "}and acknowledge the{" "}
                      <span role="button" tabIndex={0} className="linklike" data-testid="beta-privacy-toggle"
                            onClick={(e) => { e.preventDefault(); setShowPrivacy((s) => !s); }}
                            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowPrivacy((s) => !s); } }}>
                        {privacy.title.replace(/\s*\(DRAFT\)/, "")} ({privacy.version})
                      </span>.
                    </label>
                  </div>
                  {showTerms && (
                    <p className="muted beta-terms-body" data-testid="beta-terms-body">
                      {terms.body}
                    </p>
                  )}
                  {showPrivacy && (
                    <p className="muted beta-terms-body" data-testid="beta-privacy-body">
                      {privacy.body}
                    </p>
                  )}
                </div>
              )}
              <button data-testid={BETA_INVITE.submitButton} className="btn primary" disabled={busy || !agreed} onClick={submit}>
                {busy ? "…" : "Create account"}
              </button>
            </div>
          )}

          {phase === "success" && (
            <div className="card" data-testid={BETA_INVITE.success}>
              <h3><CheckCircle2 size={16} className="ok" /> Account created</h3>
              <p className="muted">
                Please check your email to verify your address, then sign in to World Wide Wallpaper.
              </p>
              <a className="btn primary" href="/" data-testid={BETA_INVITE.loginLink}>Go to sign in</a>
            </div>
          )}
        </main>
        <div className="ww-footer-standalone"><PoweredBy /></div>
      </div>
      </div>
    </>
  );
}
